import React from "react";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import baseURL from "../../Url/NodeBaseURL";
import OrderInvoice from "./templates/OrderInvoice";
import {
  amountToWords,
  buildPartyBlock,
  defaultCompanyInfo,
  formatCurrency,
  formatDate,
  isSameState,
  parseNumber,
} from "./utils/common";

const fetchEstimateDetails = async (estimateNumber) => {
  const response = await axios.get(
    `${baseURL}/Estimate/full-details/${estimateNumber}`
  );
  return response.data;
};
const fetchProducts = async () => {
  const response = await axios.get(`${baseURL}/get/products`);
  return Array.isArray(response.data) ? response.data : [];
};
const buildHsnMap = (products) =>
  products.reduce((map, product) => {
    if (product?.product_name) {
      map[product.product_name] = product.hsn_code || "";
    }
    if (product?.category) {
      map[product.category] = product.hsn_code || "";
    }
    return map;
  }, {});

const fetchUrdPurchases = async (estimateNumber) => {
  try {
    const response = await axios.get(
      `${baseURL}/get/olditems/${estimateNumber}`
    );

    if (!Array.isArray(response.data)) {
      return { purchases: [], totalAmount: 0 };
    }

    const totalAmount = response.data.reduce((sum, item) => {
      const amount = parseFloat(item.total_amount || 0);
      return sum + amount;
    }, 0);

    return { purchases: response.data, totalAmount };
  } catch (error) {
    console.warn("URD purchase fetch failed:", error);
    return { purchases: [], totalAmount: 0 };
  }
};

const fetchSalesReturns = async (estimateNumber) => {
  try {
    const response = await axios.get(
      `${baseURL}/get-estimate-sale-returns/${estimateNumber}`
    );

    if (!Array.isArray(response.data)) {
      return { returns: [], totalAmount: 0 };
    }

    const totalAmount = response.data.reduce((sum, item) => {
      const totalPrice = parseFloat(item.total_price) || 0;
      const taxAmt = parseFloat(item.tax_amt) || 0;
      const taxable =
        parseFloat(
          item.salesTaxableAmount ||
            item.sales_taxable_amt ||
            item.piece_taxable_amt ||
            item.taxable_amount ||
            0
        ) || 0;
      const hm = parseFloat(item.hm_charges) || 0;
      const net = totalPrice > 0 ? totalPrice - hm : taxable + taxAmt - hm;
      return sum + net;
    }, 0);

    return { returns: response.data, totalAmount };
  } catch (error) {
    console.warn("Sales return fetch failed:", error);
    return { returns: [], totalAmount: 0 };
  }
};

const getEstimateCore = (data) =>
  data.estimate || data.uniqueData || data.estimateData || {};

const extractLineItems = (data) =>
  data.repeatedData || data.products || data.items || [];

const buildEstimateItemRows = (lineItems, hsnMap) =>
  lineItems.map((item, index) => {
    const descriptionParts = [
      item.metal || item.metal_type,
      item.product_name,
    ].filter(Boolean);
    let description = descriptionParts.join(" - ") || "Item";
    if (item.client_visibility === "Client Facing" && item.remarks) {
      description += ` (${item.remarks})`;
    }

    // Format MC display
    let mcDisplay = "-";
    if (item.mc_per_gram != null && item.mc_per_gram !== "") {
      const metalType = (item.metal || item.metal_type || "").toLowerCase();
      if (["gold", "diamond", "others"].includes(metalType)) {
        mcDisplay = `${parseFloat(item.mc_per_gram).toFixed(2)} %`;
      } else {
        mcDisplay = parseFloat(item.mc_per_gram).toFixed(2);
      }
    }

    // Status for estimates - typically "Pending" or "Not Delivered"
    const status = "Pending";

    return {
      sno: index + 1,
      description,
      // hsn: item.hsn_code || item.hsn || "-",
      hsn: hsnMap[item.product_name] || hsnMap[item.category] || "-",
      qty: parseNumber(item.qty || item.total_wt || item.total_weight_av || 1).toFixed(2),
      purity: item.printing_purity || item.purity || "-",
      grossWeight: item.gr_wt || item.gross_weight ? parseFloat(item.gr_wt || item.gross_weight).toFixed(3) : "-",
      stoneWeight: item.st_wt || item.stone_weight ? parseFloat(item.st_wt || item.stone_weight).toFixed(3) : "-",
      netWeight: item.total_wt || item.total_weight_av ? parseFloat(item.total_wt || item.total_weight_av).toFixed(3) : "-",
      rate: formatCurrency(item.rate || item.pieace_cost || item.total_price),
      vaPercent: item.va_perc || item.va_percent || item.wastage_percent ? parseFloat(item.va_perc || item.va_percent || item.wastage_percent).toFixed(2) : "-",
      mc: mcDisplay,
      stoneAmount: formatCurrency(parseNumber(item.stone_price)),
      status: status,
      amount: formatCurrency(item.piece_taxable_amt || item.total_price),
    };
  });

const buildTaxRows = (taxAmount, subtotal, estimateInfo) => {
  if (!taxAmount) return [];

  const percent =
    subtotal && subtotal !== 0
      ? ((taxAmount / subtotal) * 100).toFixed(2)
      : parseNumber(estimateInfo.tax_percent);

  if (isSameState(estimateInfo.state, estimateInfo.state_code)) {
    const halfAmount = taxAmount / 2;
    return [
      { label: "SGST", percent: parseNumber(percent) / 2 || 0, amount: formatCurrency(halfAmount) },
      { label: "CGST", percent: parseNumber(percent) / 2 || 0, amount: formatCurrency(halfAmount) },
    ];
  }

  return [
    {
      label: "IGST",
      percent: parseNumber(percent),
      amount: formatCurrency(taxAmount),
    },
  ];
};

const buildEstimateDocument = ({
  directPrintData,
  urdPurchaseAmount,
  salesReturnAmount,
  hsnMap,
}) => {
  const estimateInfo = getEstimateCore(directPrintData);
  const lineItems = extractLineItems(directPrintData);

  const subtotal =
    parseNumber(estimateInfo.taxable_amt) ||
    lineItems.reduce(
      (sum, item) => sum + parseNumber(item.piece_taxable_amt || item.total_price || item.rate_amt),
      0
    );
  const discount = lineItems.reduce(
    (sum, item) => sum + parseNumber(item.discount_amount || item.discount || 0),
    0
  ) || parseNumber(estimateInfo.discount_amount || estimateInfo.discount || 0);
  const taxAmount =
    parseNumber(estimateInfo.tax_amount) ||
    parseNumber(estimateInfo.tax_amt) ||
    parseNumber(estimateInfo.cgst) + parseNumber(estimateInfo.sgst);
  const netAmount =
    parseNumber(estimateInfo.net_amount) ||
    parseNumber(estimateInfo.net_amt) ||
    subtotal + taxAmount;
  const netPayableAmount =
    parseNumber(estimateInfo.net_payable_amt) || netAmount;

  const notes = [
    estimateInfo.remarks,
    urdPurchaseAmount
      ? `URD Adjusted: ${formatCurrency(urdPurchaseAmount)}`
      : null,
    salesReturnAmount
      ? `Sales Return Adjusted: ${formatCurrency(salesReturnAmount)}`
      : null,
  ]
    .filter(Boolean)
    .join(" | ");

  const taxRows = buildTaxRows(taxAmount, subtotal, estimateInfo);

  return (
    <OrderInvoice
      headerTitle="Estimate"
      company={defaultCompanyInfo}
      invoiceMeta={{
        number: estimateInfo.estimate_number || "N/A",
        invoiceDate: estimateInfo.date,
        terms: estimateInfo.terms || "Custom",
        dueDate: formatDate(estimateInfo.due_date),
        placeOfSupply:
          estimateInfo.place_of_supply ||
          estimateInfo.state ||
          defaultCompanyInfo.placeOfSupply,
      }}
      billTo={buildPartyBlock(estimateInfo)}
      items={buildEstimateItemRows(lineItems, hsnMap)}
      totals={{
        subtotal: formatCurrency(subtotal),
        discount: discount > 0 ? formatCurrency(discount) : null,
        festivalOffer: null,
        taxRows: taxRows,
        total: formatCurrency(netAmount),
        oldItemsAmount: urdPurchaseAmount > 0 ? formatCurrency(urdPurchaseAmount) : null,
        schemeAmount: null,
        salesReturnAmount: salesReturnAmount > 0 ? formatCurrency(salesReturnAmount) : null,
        advanceAmount: null,
        netPayableAmount: formatCurrency(netPayableAmount),
        paymentMade: formatCurrency(0),
        balanceDue: formatCurrency(netPayableAmount),
      }}
      amountInWords={amountToWords(netAmount)}
      notes={notes || "This is a system generated estimate."}
      signature="Authorized Signature"
    />
  );
};

export const generateEstimatePdf = async (
  estimateNumber,
  { openInNewTab = true, autoPrint = true } = {}
) => {
  if (!estimateNumber) {
    throw new Error("Estimate number is required to generate PDF");
  }

  const directPrintData = await fetchEstimateDetails(estimateNumber);
  const products= await fetchProducts()
  if (!directPrintData || (!directPrintData.estimate && !directPrintData.uniqueData)) {
    throw new Error("Invalid estimate data received for PDF generation");
  }

  const [{ totalAmount: urdPurchaseAmount }, { totalAmount: salesReturnAmount }] =
    await Promise.all([
      fetchUrdPurchases(estimateNumber),
      fetchSalesReturns(estimateNumber),
    ]);

  const document = buildEstimateDocument({
    directPrintData,
    urdPurchaseAmount,
    salesReturnAmount,
    hsnMap:buildHsnMap(products),
  });

  const blob = await pdf(document).toBlob();
  const fileName = `Estimate-${estimateNumber}.pdf`;

  if (openInNewTab && typeof window !== "undefined") {
    const blobUrl = URL.createObjectURL(blob);
    const newWindow = window.open(blobUrl, "_blank");

    if (newWindow && autoPrint) {
      newWindow.onload = () => {
        newWindow.focus();
        newWindow.print();
      };
    }
  }

  return { blob, fileName };
};

export default generateEstimatePdf;

